import React, { useState, useCallback, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Select,
    MenuItem,
    TextField,
    FormControl,
    InputLabel,
    Paper,
    Divider,
    Grid,
    Card,
    CardContent,
    CardHeader,
    IconButton,
    Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik, FieldArray, Form } from "formik";
import { getAvailableAnimalsSale } from "../../api/index.js";
import { useSnackbar } from "../../utils/index.js";
import Spinner from "../Spinner.js";

const AddOrderAnimalForm = ({ onSubmit, ownerId, sellerInfo, buyerInfo }) => {
    const [fetchedMales, setFetchedMales] = useState([]);
    const [fetchedFemales, setFetchedFemales] = useState([]);
    const [fetchedLambs, setFetchedLambs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { error } = useSnackbar();

    const animalOptions = ["Προβατίνα", "Κριός", "Αμνός"];

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { animals: maleAnimals } = await getAvailableAnimalsSale(
                ownerId,
                "maleAnimal"
            );
            setFetchedMales(maleAnimals ? maleAnimals : []);

            const { animals: femaleAnimals } = await getAvailableAnimalsSale(
                ownerId,
                "femaleAnimal"
            );
            setFetchedFemales(femaleAnimals ? femaleAnimals : []);

            const { animals: lambs } = await getAvailableAnimalsSale(
                ownerId,
                "lamb"
            );
            setFetchedLambs(lambs ? lambs : []);
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    });

    useEffect(() => {
        fetchData();
    }, []);

    const initialValues = {
        animals: [{ type: "Προβατίνα", code: "", input1: "", input2: "" }],
    };

    // Helper function to get available codes based on animal type and already selected codes
    const getAvailableCodes = (animalType, selectedCodes, currentIndex) => {
        // Determine which animal list to use based on the animal type
        let animalList = [];
        if (animalType === "Προβατίνα") {
            animalList = fetchedFemales;
        } else if (animalType === "Κριός") {
            animalList = fetchedMales;
        } else {
            animalList = fetchedLambs;
        }

        // Filter out already selected codes except the current one
        return animalList.filter((item) => {
            const itemCode =
                animalType !== "Αμνός" ? item.registrationCode : item.lambSlug;
            return (
                !selectedCodes.includes(itemCode) ||
                selectedCodes.indexOf(itemCode) === currentIndex
            );
        });
    };

    return (
        <>
            <Spinner open={isLoading} />
            <Box sx={{ width: "100%", maxWidth: "1600px", mx: "auto" }}>
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "12px",
                        width: "100%"
                    }}
                >
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h4" color="primary" fontWeight="bold">
                            Φόρμα Παραγγελίας Ζώων
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 4 }} />

                    {/* Transaction Participants Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Στοιχεία Συναλλαγής
                        </Typography>

                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            {/* Seller Information */}
                            <Grid item xs={12} md={6}>
                                <Card elevation={1} sx={{ height: '100%' }}>
                                    <CardHeader
                                        title={
                                            <Typography variant="h6" color="primary">
                                                Πωλητής
                                            </Typography>
                                        }
                                        sx={{ backgroundColor: '#f0f7ff', pb: 1 }}
                                    />
                                    <CardContent>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {sellerInfo?.lastName} {sellerInfo?.firstName}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Κωδικός: {sellerInfo?.code}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    ΑΦΜ: {sellerInfo?.afm}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2">
                                                    {sellerInfo?.address}, {sellerInfo?.city} {sellerInfo?.postalCode}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2">
                                                    Τηλέφωνο: {sellerInfo?.phoneNumber || sellerInfo?.cellPhone}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Buyer Information */}
                            <Grid item xs={12} md={6}>
                                <Card elevation={1} sx={{ height: '100%' }}>
                                    <CardHeader
                                        title={
                                            <Typography variant="h6" color="primary">
                                                Αγοραστής
                                            </Typography>
                                        }
                                        sx={{ backgroundColor: '#f0f7ff', pb: 1 }}
                                    />
                                    <CardContent>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {buyerInfo?.buyerLastName} {buyerInfo?.buyerFirstName}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2">
                                                    {buyerInfo?.address}, {buyerInfo?.city} {buyerInfo?.postalCode}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2">
                                                    {buyerInfo?.county && `Νομός: ${buyerInfo.county}`}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2">
                                                    Τηλέφωνο: {buyerInfo?.phoneNumber}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ mb: 4 }} />

                    <Formik
                        initialValues={initialValues}
                        onSubmit={(values) => {
                            onSubmit(values.animals);
                        }}
                    >
                        {({ values, errors, touched, handleChange, setFieldValue }) => {
                            // Get all currently selected codes
                            const selectedCodes = values.animals
                                .map((animal) => animal.code)
                                .filter((code) => code !== "");

                            return (
                                <Form>
                                    <Box>
                                        <Box sx={{ mb: 4 }}>
                                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                                Συμπληρώστε τις λεπτομέρειες για κάθε ζώο
                                            </Typography>
                                        </Box>

                                        <FieldArray name="animals">
                                            {({ push, remove }) => (
                                                <>
                                                    {values?.animals.map((animal, index) => {
                                                        // Get available animals for this entry
                                                        const availableAnimals = getAvailableCodes(
                                                            animal.type,
                                                            selectedCodes,
                                                            index
                                                        );

                                                        return (
                                                            <Card
                                                                key={index}
                                                                elevation={2}
                                                                sx={{
                                                                    mb: 3,
                                                                    borderRadius: "8px",
                                                                    border: "1px solid #e0e0e0",
                                                                    overflow: "visible",
                                                                    width: "100%"
                                                                }}
                                                            >
                                                                <CardHeader
                                                                    title={
                                                                        <Chip
                                                                            label={`Ζώο ${index + 1}`}
                                                                            color="primary"
                                                                            sx={{ fontWeight: "bold" }}
                                                                        />
                                                                    }
                                                                    action={
                                                                        values.animals.length > 1 && (
                                                                            <IconButton
                                                                                color="error"
                                                                                onClick={() => remove(index)}
                                                                                aria-label="αφαίρεση"
                                                                            >
                                                                                <DeleteIcon />
                                                                            </IconButton>
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        backgroundColor: "#f5f5f5",
                                                                        borderBottom: "1px solid #e0e0e0",
                                                                        pb: 1
                                                                    }}
                                                                />
                                                                <CardContent sx={{ pt: 3 }}>
                                                                    <Grid container spacing={3}>
                                                                        <Grid item xs={12} sm={6} md={3} lg={2}>
                                                                            <FormControl
                                                                                fullWidth
                                                                                error={
                                                                                    errors.animals?.[index]?.type &&
                                                                                    touched.animals?.[index]?.type
                                                                                }
                                                                            >
                                                                                <InputLabel id={`type-label-${index}`}>
                                                                                    Τύπος Ζώου
                                                                                </InputLabel>
                                                                                <Select
                                                                                    labelId={`type-label-${index}`}
                                                                                    name={`animals.${index}.type`}
                                                                                    value={animal.type}
                                                                                    required={true}
                                                                                    onChange={(e) => {
                                                                                        handleChange(e);
                                                                                        // Reset code when type changes
                                                                                        setFieldValue(
                                                                                            `animals.${index}.code`,
                                                                                            ""
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    {animalOptions.map((option) => (
                                                                                        <MenuItem key={option} value={option}>
                                                                                            {option}
                                                                                        </MenuItem>
                                                                                    ))}
                                                                                </Select>
                                                                            </FormControl>
                                                                        </Grid>

                                                                        <Grid item xs={12} sm={6} md={4} lg={4}>
                                                                            <FormControl
                                                                                fullWidth
                                                                                error={
                                                                                    errors.animals?.[index]?.code &&
                                                                                    touched.animals?.[index]?.code
                                                                                }
                                                                            >
                                                                                <InputLabel id={`dropdown-label-${index}`}>
                                                                                    Κωδικός
                                                                                </InputLabel>
                                                                                <Select
                                                                                    labelId={`dropdown-label-${index}`}
                                                                                    name={`animals.${index}.code`}
                                                                                    value={animal.code}
                                                                                    onChange={handleChange}
                                                                                    required={true}
                                                                                >
                                                                                    {availableAnimals.map((item) => {
                                                                                        // Determine the code value based on animal type
                                                                                        const codeValue =
                                                                                            animal.type !== "Αμνός"
                                                                                                ? item.registrationCode
                                                                                                : item.lambSlug;

                                                                                        return (
                                                                                            <MenuItem
                                                                                                key={codeValue}
                                                                                                value={codeValue}
                                                                                            >
                                                                                                {codeValue}
                                                                                            </MenuItem>
                                                                                        );
                                                                                    })}
                                                                                </Select>
                                                                            </FormControl>
                                                                        </Grid>

                                                                        <Grid item xs={12} sm={6} md={animal.type === "Αμνός" ? 3 : 4} lg={animal.type === "Αμνός" ? 3 : 6}>
                                                                            <TextField
                                                                                name={`animals.${index}.input1`}
                                                                                label={
                                                                                    animal.type === "Αμνός"
                                                                                        ? "Κωδικός που θα λάβει στην Εκτροφή"
                                                                                        : "Τιμή (€)"
                                                                                }
                                                                                value={animal.input1}
                                                                                onChange={handleChange}
                                                                                fullWidth
                                                                                required={true}
                                                                                error={
                                                                                    errors.animals?.[index]?.input1 &&
                                                                                    touched.animals?.[index]?.input1
                                                                                }
                                                                                helperText={
                                                                                    errors.animals?.[index]?.input1 &&
                                                                                        touched.animals?.[index]?.input1
                                                                                        ? errors.animals[index].input1
                                                                                        : ""
                                                                                }
                                                                                InputProps={{
                                                                                    endAdornment: animal.type !== "Αμνός" && "€"
                                                                                }}
                                                                            />
                                                                        </Grid>

                                                                        {animal.type === "Αμνός" && (
                                                                            <Grid item xs={12} sm={6} md={3} lg={3}>
                                                                                <TextField
                                                                                    name={`animals.${index}.input2`}
                                                                                    label="Τιμή (€)"
                                                                                    value={animal.input2}
                                                                                    onChange={handleChange}
                                                                                    fullWidth
                                                                                    required={true}
                                                                                    error={
                                                                                        errors.animals?.[index]?.input2 &&
                                                                                        touched.animals?.[index]?.input2
                                                                                    }
                                                                                    helperText={
                                                                                        errors.animals?.[index]?.input2 &&
                                                                                            touched.animals?.[index]?.input2
                                                                                            ? errors.animals[index].input2
                                                                                            : ""
                                                                                    }
                                                                                    InputProps={{
                                                                                        endAdornment: "€"
                                                                                    }}
                                                                                />
                                                                            </Grid>
                                                                        )}
                                                                    </Grid>
                                                                </CardContent>
                                                            </Card>
                                                        );
                                                    })}

                                                    <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() =>
                                                                push({
                                                                    type: "Προβατίνα",
                                                                    code: "",
                                                                    input1: "",
                                                                    input2: "",
                                                                })
                                                            }
                                                            sx={{
                                                                borderRadius: "20px",
                                                                px: 3,
                                                                py: 1,
                                                                fontSize: "1rem"
                                                            }}
                                                        >
                                                            Προσθήκη Ζώου
                                                        </Button>
                                                    </Box>
                                                </>
                                            )}
                                        </FieldArray>

                                        <Divider sx={{ mt: 2, mb: 4 }} />

                                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                type="submit"
                                                size="large"
                                                sx={{
                                                    px: 4,
                                                    py: 1.5,
                                                    borderRadius: "8px",
                                                    fontSize: "1.1rem",
                                                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
                                                }}
                                            >
                                                Υποβολή Παραγγελίας
                                            </Button>
                                        </Box>
                                    </Box>
                                </Form>
                            );
                        }}
                    </Formik>
                </Paper>
            </Box>
        </>
    );
};

export default AddOrderAnimalForm;